import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    createUrlTreeFromSnapshot,
    UrlTree
} from '@angular/router';
import {
    PersonsOrganisationDto,
    UserService,
    UserStatus
} from '@flyfreely-portal-ui/flyfreely';
import {
    computeHubSpotMode,
    WorkspaceStateService
} from '@flyfreely-portal-ui/workspace';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';

/**
 * Parses a query parameter that should hold an integer, returning `undefined` when it is
 * absent or unparseable.
 */
function parseIntegerParam(value: unknown): number | undefined {
    if (value == null) {
        return undefined;
    }
    const parsed = parseInt(String(value), 10);
    return isNaN(parsed) ? undefined : parsed;
}

/**
 * This guard owns the `organisationId` and `hsmode` query parameters for the main Workspace
 * routing tree, and is the only place that writes them.
 *
 * `organisationId` selects the organisation. When it is absent the guard falls back to
 * `currentUser.defaultOrganisationId ?? currentUser.personalOrganisationId`.
 *
 * `hsmode` selects which HubSpot support widget loads, and is derived from the organisation's
 * support feature flags by {@link computeHubSpotMode}. HubSpot picks the widget from the URL,
 * so the value has to be in the URL before the widget loads, and it has to describe the
 * organisation that is actually selected.
 *
 * Both parameters are verified on every navigation, so the route needs
 * `runGuardsAndResolvers: 'always'`. Switching organisation navigates with
 * `queryParamsHandling: 'merge'`, which carries the previous organisation's `hsmode` across,
 * and this guard corrects it before the workspace reads it.
 *
 */
@Injectable()
export class OrganisationRedirectGuard {
    constructor(
        private userService: UserService,
        private workspaceStateService: WorkspaceStateService
    ) {}

    async canActivate(
        route: ActivatedRouteSnapshot
    ): Promise<boolean | UrlTree> {
        const user = await firstValueFrom(
            this.userService.userChange$.pipe(
                filter(
                    c =>
                        c.type !== UserStatus.UNKNOWN &&
                        c.type !== UserStatus.LOADING
                )
            )
        );

        // `AuthenticatedGuard` runs alongside this guard and owns the login redirect. Bail out
        // rather than wait for a login that this guard cannot trigger.
        if (user.type !== UserStatus.LOGGED_IN) {
            return true;
        }

        const requestedOrganisationId = parseIntegerParam(
            route.queryParams.organisationId
        );

        const organisationId =
            requestedOrganisationId ??
            user.currentUser.defaultOrganisationId ??
            user.currentUser.personalOrganisationId;

        const organisation = await this.findOrganisation(organisationId);

        if (organisation == null) {
            // The organisation could not be loaded, so `hsmode` cannot be derived. Still make
            // sure `organisationId` is present, because the workspace requires it. Reporting
            // and redirecting the no access case belongs to `AccountPermissionsGuard`.
            return requestedOrganisationId === organisationId
                ? true
                : createUrlTreeFromSnapshot(route, [], {
                      ...route.queryParams,
                      organisationId
                  });
        }

        const hsmode = computeHubSpotMode(organisation);

        if (
            requestedOrganisationId === organisationId &&
            parseIntegerParam(route.queryParams.hsmode) === hsmode
        ) {
            return true;
        }

        return createUrlTreeFromSnapshot(route, [], {
            ...route.queryParams,
            organisationId,
            hsmode
        });
    }

    /**
     * Finds the organisation so its support flags can be read.
     *
     * System admins can select an organisation outside their own list, which has to be fetched.
     * {@link WorkspaceStateService.changeOrganisation} starts that fetch, so this guard triggers
     * it rather than waiting on a load that nothing else has asked for yet.
     *
     * @returns the organisation, or `null` when the user has no access to it
     */
    private async findOrganisation(
        organisationId: number
    ): Promise<PersonsOrganisationDto | null> {
        const knownOrganisation = this.userService
            .findUsersOrganisations()
            .find(o => o.id === organisationId);

        if (knownOrganisation != null) {
            return knownOrganisation;
        }

        this.workspaceStateService.changeOrganisation(organisationId);

        const currentOrganisation = await firstValueFrom(
            this.workspaceStateService.currentOrganisation$.pipe(
                filter(o => o.type !== 'loading'),
                take(1)
            )
        );

        // `no_licence` still carries the organisation, and those users get a support widget too.
        return currentOrganisation.type === 'organisation_loaded' ||
            currentOrganisation.type === 'no_licence'
            ? currentOrganisation.organisation
            : null;
    }
}
